/**
 * Large full-width Yes/No style buttons for single-select / radio questions.
 * Kept separate from the shared RadioButton (used by Variant A) so restyling
 * here doesn't affect the control variant.
 */

import React from "react";
import GenderMale from "../../../icons/GenderMale.svg";
import GenderFemale from "../../../icons/GenderFemale.svg";
import { orderAnswerOptionsChildren } from "../components/helpers";
import { useShellIdFn } from "./shellContext";
import { GENDER_VALUES, HIGHLIGHT_WORDS } from "./constants";

interface SingleSelectAnswersProps {
  answersGroup: any;
  answersSelectedMap: Record<string | number, any>;
  onSelect: (child: any) => void;
  errorMode?: boolean;
}

const GENDER_ICON_MAP: Record<string, string> = {
  ...Object.fromEntries(GENDER_VALUES.male.map((v) => [v, GenderMale])),
  ...Object.fromEntries(GENDER_VALUES.female.map((v) => [v, GenderFemale])),
};

// Highlights frequency words (HIGHLIGHT_WORDS) wherever they appear in an
// answer option's own text (e.g. "A weekly injection" vs "A daily injection")
// so the two options stay visually distinguishable. Option copy is trusted
// backend catalog content already rendered raw elsewhere (QuestionFrame.tsx's
// titleHtml/subtitleHtml), so wrapping a substring in a static hardcoded
// class is safe.
function highlightFrequencyWords(value: string): string {
  let html = value;
  for (const word of HIGHLIGHT_WORDS) {
    const re = new RegExp(`\\b${word}\\b`, "gi");
    html = html.replace(re, (match) => `<span class="text-[#f59200] font-semibold">${match}</span>`);
  }
  return html;
}

export function pinsSingleSelectAnswersToBottom(answersGroup: any, helpBlock?: string): boolean {
  const count = answersGroup?.children?.length ?? 0;
  return (count >= 2 && count <= 3) || /<li[\s>]/i.test(helpBlock ?? "");
}

const SingleSelectAnswers: React.FC<SingleSelectAnswersProps> = ({
  answersGroup,
  answersSelectedMap,
  onSelect,
  errorMode,
}) => {
  // Both shells render this list, so scope the ids to keep them unique.
  const scopeId = useShellIdFn();

  const children = orderAnswerOptionsChildren(
    answersGroup?.children || [],
    answersGroup?.recipe?.recipe_question_id
  );

  // Slightly shorter cards for Yes/No pairs (conditions list) — still readable.
  const yesNoPair = children.length === 2;

  return (
    <div id={scopeId("wl2_single_select_answers")} className={`flex flex-col ${yesNoPair ? "gap-3" : "gap-4"}`}>
      {children.map((child: any) => {
        const selected = !!answersSelectedMap[child.recipe.id];
        const invalid = !!errorMode && !selected;
        const icon = GENDER_ICON_MAP[String(child.recipe.value).trim().toLowerCase()];
        return (
          <button
            key={child.recipe.id}
            id={scopeId(`answer_${child.recipe.id}`)}
            type="button"
            onClick={() => onSelect(child)}
            className={
              yesNoPair
                ? "w-full min-h-[60px] px-5 py-3 rounded-[14px] font-['Poppins'] font-normal text-[16px] leading-6 flex items-center justify-center cursor-pointer hover:shadow-md active:scale-[0.98] transition-transform duration-100"
                : "w-full min-h-[64px] px-5 py-3 rounded-[16px] font-['Poppins'] text-[18px] flex items-center justify-center cursor-pointer hover:shadow-md active:scale-[0.96] transition-transform duration-100"
            }
            // min-h + padding: long labels wrap to two lines and the card grows
            // instead of the text touching the edges of a fixed-height card.
            style={{
              border: `1px solid ${
                selected ? "#f59200" : invalid ? "var(--primary-danger)" : yesNoPair ? "#dee3ed" : "#e5e7eb"
              }`,
              backgroundColor: selected ? "#fff7ec" : invalid ? "var(--sub-danger)" : "#ffffff",
              color: invalid ? "var(--primary-danger)" : yesNoPair ? "#08080a" : "#333",
            }}
          >
            <span className="flex items-center gap-[5px]">
              {icon && <img src={icon} alt="" className="size-[24px]" />}
              <span dangerouslySetInnerHTML={{ __html: highlightFrequencyWords(String(child.recipe.value)) }} />
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default SingleSelectAnswers;
